// Browser-compatible URL parsing polyfill for Node.js 'url' module
export const parse = (urlString, parseQueryString = false) => {
  // Handle relative URLs (starting with /) as relative paths
  if (urlString.startsWith('/')) {
    const [pathname, search] = urlString.split('?');
    const searchParams = search ? new URLSearchParams(search) : new URLSearchParams();

    return {
      protocol: null,
      slashes: false,
      auth: null,
      host: null,
      port: null,
      hostname: null,
      hash: null,
      search: search ? '?' + search : null,
      query: parseQueryString ? Object.fromEntries(searchParams) : search ? '?' + search : null,
      pathname: pathname,
      path: urlString,
      href: urlString,
    };
  }

  try {
    const url = new URL(urlString);
    return {
      protocol: url.protocol,
      slashes: url.protocol.includes('://'),
      auth: null,
      host: url.host,
      port: url.port || null,
      hostname: url.hostname,
      hash: url.hash || null,
      search: url.search || null,
      query: parseQueryString ? Object.fromEntries(url.searchParams) : url.search || null,
      pathname: url.pathname,
      path: url.pathname + (url.search || ''),
      href: url.href,
    };
  } catch (e) {
    // Fallback for other invalid URLs
    return {
      protocol: null,
      slashes: false,
      auth: null,
      host: null,
      port: null,
      hostname: null,
      hash: null,
      search: null,
      query: parseQueryString ? {} : null,
      pathname: urlString,
      path: urlString,
      href: urlString,
    };
  }
};

export const format = urlObject => {
  if (typeof urlObject === 'string') return urlObject;

  // If it's a relative URL (no protocol/host), just return the path
  if (!urlObject.protocol && !urlObject.host && !urlObject.hostname) {
    let url = urlObject.pathname || '';

    if (urlObject.search) {
      url += urlObject.search;
    } else if (urlObject.query && typeof urlObject.query === 'object') {
      const searchParams = new URLSearchParams(urlObject.query);
      const search = searchParams.toString();
      if (search) {
        url += '?' + search;
      }
    }

    if (urlObject.hash) {
      url += urlObject.hash;
    }

    return url || urlObject.path || '';
  }

  // For absolute URLs, build the full URL
  let url = '';

  if (urlObject.protocol) {
    url += urlObject.protocol;
    if (urlObject.slashes || urlObject.protocol.includes('://')) {
      if (!urlObject.protocol.endsWith('://')) {
        url += '//';
      }
    }
  }

  if (urlObject.auth) {
    url += urlObject.auth + '@';
  }

  if (urlObject.hostname) {
    url += urlObject.hostname;
    if (urlObject.port) {
      url += ':' + urlObject.port;
    }
  } else if (urlObject.host) {
    url += urlObject.host;
  }

  if (urlObject.pathname) {
    url += urlObject.pathname;
  }

  if (urlObject.search) {
    url += urlObject.search;
  } else if (urlObject.query && typeof urlObject.query === 'object') {
    const searchParams = new URLSearchParams(urlObject.query);
    const search = searchParams.toString();
    if (search) {
      url += '?' + search;
    }
  }

  if (urlObject.hash) {
    url += urlObject.hash;
  }

  return url || urlObject.pathname || urlObject.path || '';
};

export const resolve = (from, to) => {
  try {
    return new URL(to, from).href;
  } catch (e) {
    return to;
  }
};

// Default export for compatibility
export default { parse, format, resolve };
